import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/base/Breadcrumb';
import { siteConfig, aboutContent } from '@/mocks/site';

const reputationImages: Record<number, string> = {
  0: 'https://readdy.ai/api/search-image?query=Professional%20Chinese%20scholar%20portrait%2C%20middle%20aged%20man%2C%20warm%20lighting%2C%20academic%20setting%2C%20neutral%20expression%2C%20professional%20headshot&width=200&height=200&seq=reputation-01&orientation=squarish',
  1: 'https://readdy.ai/api/search-image?query=Chinese%20magazine%20editor%20portrait%2C%20professional%20woman%2C%20warm%20lighting%2C%20publishing%20office%20background%2C%20friendly%20expression%2C%20headshot&width=200&height=200&seq=reputation-02&orientation=squarish',
  2: 'https://readdy.ai/api/search-image?query=Chinese%20university%20researcher%20portrait%2C%20young%20scholar%2C%20library%20background%2C%20warm%20lighting%2C%20thoughtful%20expression%2C%20professional%20headshot&width=200&height=200&seq=reputation-03&orientation=squarish',
};

export default function AboutPage() {
  const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const honeypot = (form.elements.namedItem('website_alt') as HTMLInputElement)?.value?.trim();
    if (honeypot) {
      setFormStatus('error');
      setFormError('提交失败，请稍后重试。');
      return;
    }
    // Use mock success for now - form will be connected later
    setFormStatus('success');
    form.reset();
    setTimeout(() => setFormStatus('idle'), 3000);
  };

  const breadcrumbItems = [
    { label: '首页', href: '/' },
    { label: '关于我们' },
  ];

  const orgJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: siteConfig.name,
    url: siteConfig.url,
    description: siteConfig.description,
    email: siteConfig.email,
    foundingDate: siteConfig.foundingYear,
    contactPoint: {
      '@type': 'ContactPoint',
      contactType: 'customer service',
      email: siteConfig.email,
    },
  };

  const webPageJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    name: `关于我们 - ${siteConfig.name}`,
    description: siteConfig.description,
    url: `${siteConfig.url}/about`,
  };

  return (
    <>
      <Navbar />
      <main>
        <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(orgJsonLd) }} />
        <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(webPageJsonLd) }} />

        <section className="pt-20 pb-10 md:pb-14 bg-background-100">
          <div className="px-4 md:px-6 max-w-4xl mx-auto">
            <div className="mb-4">
              <Breadcrumb items={breadcrumbItems} />
            </div>
            <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-3">
              {aboutContent.brandIntro.title}
            </h1>
            <div
              className="text-sm md:text-base text-foreground-600 leading-relaxed space-y-4"
              dangerouslySetInnerHTML={{ __html: aboutContent.brandIntro.content }}
            />
          </div>
        </section>

        <section className="py-14 md:py-20 bg-background-50">
          <div className="px-4 md:px-6 max-w-4xl mx-auto">
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-3 text-center">
              {aboutContent.reputation.title}
            </h2>
            <p className="text-sm text-foreground-500 text-center mb-10 md:mb-12">
              来自学界与业界的高度认可
            </p>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
              {aboutContent.reputation.items.map((item, index) => (
                <div
                  key={index}
                  className="bg-background-100 rounded-lg p-6 md:p-7 flex flex-col items-center text-center"
                >
                  <div className="w-16 h-16 rounded-full overflow-hidden mb-4">
                    <img
                      src={reputationImages[index] || reputationImages[0]}
                      alt={item.author}
                      className="w-full h-full object-cover"
                      loading="lazy"
                    />
                  </div>
                  <blockquote className="text-sm text-foreground-600 leading-relaxed mb-4 italic">
                    「{item.quote}」
                  </blockquote>
                  <div>
                    <p className="text-sm font-semibold text-foreground-800">{item.author}</p>
                    <p className="text-xs text-foreground-500">{item.role}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="py-14 md:py-20 bg-background-100">
          <div className="px-4 md:px-6 max-w-4xl mx-auto">
            <div className="text-center mb-10 md:mb-12">
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-3">
                {aboutContent.contact.title}
              </h2>
              <p className="text-sm text-foreground-500 max-w-lg mx-auto">
                {aboutContent.contact.description}
              </p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 md:gap-10">
              <div className="space-y-5">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 flex-shrink-0 flex items-center justify-center rounded-lg bg-primary-100 text-primary-600">
                    <i className="ri-mail-line text-lg"></i>
                  </div>
                  <div>
                    <h3 className="text-sm font-semibold text-foreground-800 mb-1">电子邮箱</h3>
                    <p className="text-sm text-foreground-500">{aboutContent.contact.email}</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 flex-shrink-0 flex items-center justify-center rounded-lg bg-accent-100 text-accent-600">
                    <i className="ri-wechat-line text-lg"></i>
                  </div>
                  <div>
                    <h3 className="text-sm font-semibold text-foreground-800 mb-1">微信公众号</h3>
                    <p className="text-sm text-foreground-500">{aboutContent.contact.wechat}</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 flex-shrink-0 flex items-center justify-center rounded-lg bg-secondary-100 text-secondary-600">
                    <i className="ri-map-pin-line text-lg"></i>
                  </div>
                  <div>
                    <h3 className="text-sm font-semibold text-foreground-800 mb-1">地址</h3>
                    <p className="text-sm text-foreground-500">{aboutContent.contact.address}</p>
                  </div>
                </div>
              </div>

              <div className="bg-background-50 rounded-lg p-6 md:p-7 border border-background-200/70">
                <h3 className="font-heading text-base font-semibold text-foreground-900 mb-5">
                  发送消息
                </h3>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <label htmlFor="name" className="block text-xs font-medium text-foreground-700 mb-1.5">
                      姓名
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      required
                      className="w-full px-3 py-2 text-sm border border-background-200 rounded-md bg-background-50 text-foreground-800 focus:outline-none focus:border-primary-400 transition-colors duration-200"
                    />
                  </div>
                  <div>
                    <label htmlFor="email" className="block text-xs font-medium text-foreground-700 mb-1.5">
                      邮箱
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      required
                      className="w-full px-3 py-2 text-sm border border-background-200 rounded-md bg-background-50 text-foreground-800 focus:outline-none focus:border-primary-400 transition-colors duration-200"
                    />
                  </div>
                  <div>
                    <label htmlFor="message" className="block text-xs font-medium text-foreground-700 mb-1.5">
                      留言
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      rows={4}
                      required
                      maxLength={500}
                      className="w-full px-3 py-2 text-sm border border-background-200 rounded-md bg-background-50 text-foreground-800 focus:outline-none focus:border-primary-400 transition-colors duration-200 resize-none"
                    ></textarea>
                    <p className="text-xs text-foreground-400 mt-1">最多500字</p>
                  </div>
                  <input
                    type="text"
                    name="website_alt"
                    className="form-hp-field"
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                    readOnly
                  />
                  <button
                    type="submit"
                    className="w-full px-5 py-2.5 bg-primary-500 text-background-50 rounded-md text-sm font-medium hover:bg-primary-600 transition-all duration-300 whitespace-nowrap cursor-pointer"
                  >
                    发送消息
                  </button>
                  {formStatus === 'success' && (
                    <p className="text-sm text-secondary-700 text-center">消息已发送，感谢您的来信！</p>
                  )}
                  {formStatus === 'error' && (
                    <p className="text-sm text-primary-600 text-center">{formError}</p>
                  )}
                </form>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
      <ScrollToTop />
    </>
  );
}